import { create } from 'zustand';
import { initAnalytics } from '../../services/firebase/config';

/* ============================================================
   Store du consentement cookies — choix persisté en
   localStorage (aucun backend). `analytics === null` = choix
   jamais fait → la bannière de consentement s'affiche.
   Le flag `analytics` pilote Google Analytics : acceptation →
   initAnalytics() (prod uniquement, voir services/firebase/config) ;
   refus → aucune injection au prochain chargement (une instance
   déjà chargée dans la session courante ne peut pas être retirée,
   le refus prend pleinement effet au rechargement).
   ============================================================ */

const STORAGE_KEY = 'rabbit_cookie_consent';

interface StoredConsent {
  analytics: boolean;
  decidedAt: string;
}

function loadStoredConsent(): boolean | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as StoredConsent).analytics === true;
  } catch {
    return null;
  }
}

function persistConsent(analytics: boolean): void {
  try {
    const consent: StoredConsent = { analytics, decidedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Stockage indisponible (mode privé…) : le choix vaut pour la session.
  }
}

interface CookieConsentState {
  /** null = pas de choix enregistré (bannière visible) */
  analytics: boolean | null;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  savePreferences: (analytics: boolean) => void;
}

export const useCookieConsentStore = create<CookieConsentState>((set) => ({
  analytics: loadStoredConsent(),
  acceptAll: () => {
    persistConsent(true);
    set({ analytics: true });
    initAnalytics();
  },
  rejectNonEssential: () => {
    persistConsent(false);
    set({ analytics: false });
  },
  savePreferences: (analytics) => {
    persistConsent(analytics);
    set({ analytics });
    if (analytics) initAnalytics();
  },
}));
